import type { DateRange } from "@/hooks/useDateRange";
import {
  differenceInCalendarDays,
  endOfDay,
  format,
  startOfDay,
  subDays,
} from "date-fns";

function getDefaultDateRange(): DateRange {
  const now = new Date();
  return {
    from: startOfDay(subDays(now, 29)),
    to: endOfDay(now),
  };
}

export function getPreviousDateRange(range: DateRange | undefined): DateRange {
  const fallback = getDefaultDateRange();
  const fromDate = startOfDay(range?.from ?? fallback.from!);
  const toDate = endOfDay(range?.to ?? range?.from ?? fallback.to!);

  const dayCount = differenceInCalendarDays(toDate, fromDate) + 1;
  const previousTo = endOfDay(subDays(fromDate, 1));
  const previousFrom = startOfDay(subDays(fromDate, dayCount));

  return {
    from: previousFrom,
    to: previousTo,
  };
}

export function toIsoDateRange(range?: DateRange): {
  from: string;
  to: string;
} {
  const fallback = getDefaultDateRange();
  const fromDate = startOfDay(range?.from ?? fallback.from!);
  const toDate = endOfDay(range?.to ?? range?.from ?? fallback.to!);

  return {
    from: fromDate.toISOString(),
    to: toDate.toISOString(),
  };
}

export function formatPeriodLabel(range?: DateRange): string {
  const fallback = getDefaultDateRange();
  const fromDate = range?.from ?? fallback.from!;
  const toDate = range?.to ?? range?.from ?? fallback.to!;

  if (fromDate.getFullYear() === toDate.getFullYear()) {
    if (
      fromDate.getMonth() === toDate.getMonth() &&
      fromDate.getDate() === toDate.getDate()
    ) {
      return format(fromDate, "MMMM d, yyyy");
    }

    if (fromDate.getMonth() === toDate.getMonth()) {
      return `${format(fromDate, "MMMM d")} - ${format(toDate, "d, yyyy")}`;
    }

    return `${format(fromDate, "MMMM d")} - ${format(toDate, "MMMM d, yyyy")}`;
  }

  return `${format(fromDate, "MMM d, yyyy")} - ${format(toDate, "MMM d, yyyy")}`;
}

export function getPeriodContextKey(dateRange?: DateRange): string {
  if (dateRange?.from && dateRange?.to) {
    return "analytics.admin.totalRevenue.periodContext.custom";
  }

  return "analytics.admin.totalRevenue.periodContext.last30Days";
}
